/**
 * homeContent.ts — per-country home-page content (hero, chips, routes).
 *
 * USER DIRECTION: "the regions and the background image will be location
 * specific" — everything the home page shows must resolve per country.
 *
 * RULES:
 * - AU content is the pre-existing Camping Australia content and must stay
 *   BYTE-IDENTICAL — it is asserted verbatim in homeContent.test.ts. Never
 *   reword an AU string here without updating that regression test on purpose.
 * - On camping-australia the region chips run the `region:<name>` graph search
 *   (AU has Region nodes). On escapeto, region chips are plain LOCATION
 *   searches (`searchQuery`) — non-AU countries have no Region nodes, so the
 *   chips must go through the geocoded location-search path.
 * - Hero background: AU has an owned, licensed photo in public/images. No
 *   licensed US/CA/NZ photography is wired yet, so those countries ship a
 *   scenery-toned CSS gradient (heroGradient) instead of the AU beach photo —
 *   deliberately never leak the kangaroo-beach shot onto /us. Swap in a real
 *   photo by setting heroImage once licensed imagery lands in public/images.
 */
import { getCountry, type Country } from './brand';

export interface RegionChip {
  /** Short chip text. */
  label: string;
  /**
   * What the chip searches for.
   * - camping-australia: must match a Region name (run as `region:<name>`).
   * - escapeto: a geocodable place name (run as a plain location search).
   */
  searchQuery: string;
}

export interface RouteChip {
  /** Chip text shown to the user (AU: the raw "X to Y" string). */
  label: string;
  /** "start to end [via a, b]" query fed to the route search parser. */
  query: string;
}

export interface CountryHomeContent {
  /** public/ path for the hero background photo (omit → use heroGradient). */
  heroImage?: string;
  /** CSS background for countries without licensed hero photography yet. */
  heroGradient?: string;
  popularRegions: RegionChip[];
  popularSearches: string[];
  popularRoutes: RouteChip[];
  /** "No results found" empty-state suggestion chips. */
  emptySuggestions: string[];
  /** Trip-assistant example prompts (default mode). */
  assistantSuggestions: string[];
  /** Trip-assistant example prompts while a route search is active. */
  assistantRouteSuggestions: string[];
}

const AU: CountryHomeContent = {
  heroImage: '/images/camping-hero.webp',
  popularRegions: [
    { label: 'Blue Mountains', searchQuery: 'Blue Mountains' },
    { label: 'Grampians', searchQuery: 'Grampians' },
    { label: 'Great Ocean Road', searchQuery: 'Great Ocean Road' },
    { label: 'Gippsland', searchQuery: 'Gippsland' },
    { label: 'Flinders Ranges', searchQuery: 'Flinders Ranges' },
    { label: 'Hunter Valley', searchQuery: 'Hunter Valley' },
    { label: 'Snowy Mountains', searchQuery: 'Snowy Mountains' },
    { label: 'Sunshine Coast', searchQuery: 'Sunshine Coast' },
    { label: 'Margaret River', searchQuery: 'Margaret River & South West WA' },
    { label: 'Far North QLD', searchQuery: 'Far North Queensland' },
  ],
  popularSearches: [
    'Byron Bay',
    'Margaret River',
    'Noosa',
    'Great Ocean Road',
    'Kimberley',
    'Bright',
    'Jervis Bay',
    'Blue Mountains',
  ],
  popularRoutes: [
    'Sydney to Byron Bay',
    'Melbourne to Adelaide',
    'Cairns to Cape Tribulation',
    'Perth to Esperance',
    'Adelaide to Alice Springs',
    'Hobart to Cradle Mountain',
  ].map((route) => ({ label: route, query: route })),
  emptySuggestions: ['Byron Bay', 'Noosa', 'Margaret River'],
  assistantSuggestions: [
    'Plan a trip from Melbourne to Adelaide',
    'Set the search radius to 120 km',
    'Find free camping with toilets near Byron Bay',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly campsite near Bright',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

const US: CountryHomeContent = {
  // Red-rock canyon dusk — layered like the AU photo treatment so the white
  // hero copy and glass search card read identically.
  heroGradient:
    'radial-gradient(120% 90% at 85% 10%, rgba(255,196,120,0.35) 0%, rgba(255,196,120,0) 55%), linear-gradient(165deg, #17263f 0%, #3d3357 28%, #7c4a44 58%, #b96a45 80%, #d99157 100%)',
  popularRegions: [
    { label: 'Yosemite', searchQuery: 'Yosemite National Park' },
    { label: 'Yellowstone', searchQuery: 'Yellowstone National Park' },
    { label: 'Great Smoky Mountains', searchQuery: 'Great Smoky Mountains' },
    { label: 'Moab & Arches', searchQuery: 'Moab' },
    { label: 'Pacific Northwest', searchQuery: 'Olympic Peninsula' },
    { label: 'Colorado Rockies', searchQuery: 'Rocky Mountain National Park' },
    { label: 'Adirondacks', searchQuery: 'Adirondacks' },
    { label: 'Joshua Tree', searchQuery: 'Joshua Tree National Park' },
    { label: 'Glacier NP', searchQuery: 'Glacier National Park' },
    { label: 'Florida Keys', searchQuery: 'Florida Keys' },
  ],
  popularSearches: ['Moab', 'Lake Tahoe', 'Asheville', 'Bend', 'Sedona'],
  popularRoutes: [
    { label: 'Pacific Coast Highway', query: 'San Francisco to Los Angeles via Monterey, Big Sur' },
    { label: 'Blue Ridge Parkway', query: 'Waynesboro to Cherokee via Asheville' },
    { label: "Utah's Mighty 5", query: 'Zion National Park to Arches National Park via Bryce Canyon, Capitol Reef' },
    { label: 'Route 66 West', query: 'Albuquerque to Los Angeles via Flagstaff' },
  ],
  emptySuggestions: ['Moab', 'Lake Tahoe', 'Asheville'],
  assistantSuggestions: [
    'Plan a trip from Denver to Moab',
    'Set the search radius to 75 miles',
    'Find free camping with toilets near Lake Tahoe',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly campground near Sedona',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

const CA_CONTENT: CountryHomeContent = {
  // Pine forest over a glacial lake — deep teal into spruce green.
  heroGradient:
    'radial-gradient(110% 80% at 80% 8%, rgba(164,216,196,0.30) 0%, rgba(164,216,196,0) 55%), linear-gradient(165deg, #0d2431 0%, #123f46 32%, #1e5d55 62%, #35795f 82%, #6fa06a 100%)',
  popularRegions: [
    { label: 'Banff & Lake Louise', searchQuery: 'Banff' },
    { label: 'Jasper', searchQuery: 'Jasper' },
    { label: 'Whistler & Sea-to-Sky', searchQuery: 'Whistler' },
    { label: 'Vancouver Island', searchQuery: 'Vancouver Island' },
    { label: 'Muskoka', searchQuery: 'Muskoka' },
    { label: 'Algonquin', searchQuery: 'Algonquin Provincial Park' },
    { label: 'Laurentians', searchQuery: 'Laurentides' },
    { label: 'Cape Breton', searchQuery: 'Cape Breton' },
  ],
  popularSearches: ['Banff', 'Tofino', 'Whistler', 'Muskoka', 'Canmore'],
  popularRoutes: [
    { label: 'Icefields Parkway', query: 'Banff to Jasper via Lake Louise' },
    { label: 'Cabot Trail', query: 'Baddeck to Ingonish via Cheticamp' },
    { label: 'Sea to Sky', query: 'Vancouver to Whistler via Squamish' },
  ],
  emptySuggestions: ['Banff', 'Tofino', 'Muskoka'],
  assistantSuggestions: [
    'Plan a trip from Calgary to Banff',
    'Set the search radius to 120 km',
    'Find free camping with toilets near Whistler',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly campground near Canmore',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

const NZ: CountryHomeContent = {
  // Southern Alps over a fiord — alpine blue into glacier teal.
  heroGradient:
    'radial-gradient(110% 80% at 82% 10%, rgba(190,227,230,0.32) 0%, rgba(190,227,230,0) 55%), linear-gradient(165deg, #0f2138 0%, #17405c 35%, #2a6b80 65%, #4d98a4 85%, #9fd0cc 100%)',
  popularRegions: [
    { label: 'Queenstown & Wanaka', searchQuery: 'Queenstown' },
    { label: 'Fiordland', searchQuery: 'Fiordland' },
    { label: 'Abel Tasman', searchQuery: 'Abel Tasman' },
    { label: 'Coromandel', searchQuery: 'Coromandel' },
    { label: 'Bay of Islands', searchQuery: 'Bay of Islands' },
    { label: 'Tongariro', searchQuery: 'Tongariro' },
    { label: 'West Coast Glaciers', searchQuery: 'Franz Josef' },
  ],
  popularSearches: ['Queenstown', 'Wanaka', 'Rotorua', 'Taupo', 'Nelson'],
  popularRoutes: [
    { label: 'Southern Scenic Route', query: 'Queenstown to Dunedin via Te Anau, Invercargill' },
    { label: 'Great Alpine Highway', query: "Christchurch to Greymouth via Arthur's Pass" },
  ],
  emptySuggestions: ['Queenstown', 'Wanaka', 'Rotorua'],
  assistantSuggestions: [
    'Plan a trip from Christchurch to Queenstown',
    'Set the search radius to 120 km',
    'Find free camping with toilets near Wanaka',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly holiday park near Te Anau',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

const AR: CountryHomeContent = {
  // Patagonian lake under the Andes — steppe dusk into glacial blue.
  heroGradient:
    'radial-gradient(115% 85% at 82% 10%, rgba(180,206,232,0.30) 0%, rgba(180,206,232,0) 55%), linear-gradient(165deg, #10233d 0%, #1c3f63 32%, #2f6a86 62%, #4f95a0 84%, #a7ccbf 100%)',
  // escapeto has no :Region nodes outside AU, so these run as plain geocoded
  // LOCATION searches (searchQuery), not `region:<name>` graph lookups.
  popularRegions: [
    { label: 'Bariloche & Nahuel Huapi', searchQuery: 'Bariloche' },
    { label: 'Mendoza & Uco Valley', searchQuery: 'Mendoza' },
    { label: 'El Chaltén & Los Glaciares', searchQuery: 'El Chaltén' },
    { label: 'Ushuaia & Tierra del Fuego', searchQuery: 'Ushuaia' },
    { label: 'Córdoba Sierras', searchQuery: 'Córdoba' },
    { label: 'Salta & the Northwest', searchQuery: 'Salta' },
    { label: 'Patagonia Ruta 40', searchQuery: 'Esquel' },
  ],
  popularSearches: ['Bariloche', 'Mendoza', 'El Calafate', 'Ushuaia'],
  popularRoutes: [
    { label: 'Ruta 40 Patagonia', query: 'Bariloche to El Calafate via El Chaltén' },
    { label: 'Seven Lakes Route', query: 'Bariloche to San Martín de los Andes via Villa La Angostura' },
  ],
  emptySuggestions: ['Bariloche', 'Mendoza', 'Ushuaia'],
  assistantSuggestions: [
    'Plan a trip from Bariloche to El Calafate',
    'Set the search radius to 120 km',
    'Find free camping with toilets near Bariloche',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly campsite near El Chaltén',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

const CL: CountryHomeContent = {
  // Atacama-to-Andes — high-desert terracotta into altiplano blue.
  heroGradient:
    'radial-gradient(115% 85% at 84% 10%, rgba(240,205,160,0.32) 0%, rgba(240,205,160,0) 55%), linear-gradient(165deg, #16233c 0%, #3a3556 30%, #7a4a49 60%, #b56a46 82%, #d99257 100%)',
  popularRegions: [
    { label: 'Torres del Paine', searchQuery: 'Torres del Paine' },
    { label: 'Carretera Austral', searchQuery: 'Coyhaique' },
    { label: 'Atacama Desert', searchQuery: 'San Pedro de Atacama' },
    { label: 'Lake District', searchQuery: 'Pucón' },
    { label: 'Chiloé', searchQuery: 'Chiloé' },
    { label: 'Valle de Elqui', searchQuery: 'Valle de Elqui' },
    { label: 'Aysén', searchQuery: 'Puerto Aysén' },
  ],
  popularSearches: ['Pucón', 'Puerto Varas', 'San Pedro de Atacama', 'Puerto Natales'],
  popularRoutes: [
    { label: 'Carretera Austral', query: 'Puerto Montt to Coyhaique via Chaitén' },
    { label: 'Lake District Loop', query: 'Puerto Varas to Pucón via Villarrica' },
  ],
  emptySuggestions: ['Pucón', 'Puerto Varas', 'San Pedro de Atacama'],
  assistantSuggestions: [
    'Plan a trip from Puerto Varas to Pucón',
    'Set the search radius to 120 km',
    'Find free camping with toilets near Pucón',
  ],
  assistantRouteSuggestions: [
    'Add a pet-friendly campground near Puerto Varas',
    'Make the corridor width 80 km',
    'Remove the last stop from the trip',
  ],
};

export const HOME_CONTENT: Record<Country, CountryHomeContent> = {
  AU,
  US,
  CA: CA_CONTENT,
  NZ,
  AR,
  CL,
};

/** Home content for the active country — resolve at render time, never cache. */
export function getHomeContent(): CountryHomeContent {
  return HOME_CONTENT[getCountry()];
}
